import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Animated,
  FlatList,
  Image,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Shadow, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';
import type { TonyteActivity } from '../types';
import { useActivityInterested } from '../hooks/useFeed';
import { useI18n } from '../i18n';
import { useTranslatedActivity } from '../hooks/useTranslatedActivity';
import { getWebFallbackImage, resolveActivityImage } from '../utils/activityImages';
import { getResolvedEventId } from '../utils/eventIds';
import { ensureInterestedAndAddToCalendar, ensureInterestedAndJoinEventChat, getLumaRegistrationUrl, isEventChatExpiredError, registerOnLumaThen } from '../utils/activityActions';
import { requestNotificationPermission } from '../utils/permissions';
import { showTonyteToast } from '../utils/tonyteToast';
import TonyteModal from './TonyteModal';

export type CarouselStatus = 'ready' | 'loading' | 'error';

export const CAROUSEL_CARD_WIDTH = 292;
const CARD_ASPECT = 0.64;
const CARD_GAP = 14;
const ARROW_STEP_CARDS = 2;

export const getCarouselCardHeight = (cardWidth: number) => Math.round(cardWidth * CARD_ASPECT);

function formatCardDateTime(activity: TonyteActivity, locale: string): string {
  const date = activity.startDate
    ? new Date(`${activity.startDate}T12:00:00`).toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    : '';
  const time = activity.startTime?.trim() || '';
  return [date, time].filter(Boolean).join(' · ');
}

const LOCALES: Record<string, string> = { de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' };

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

type CardProps = {
  activity: TonyteActivity;
  userId: string | null;
  width: number;
  height: number;
  showPartner?: boolean;
  onPress: (activity: TonyteActivity) => void;
  onOpenChat: (activity: TonyteActivity) => void;
  // Admin/co-admin curation button (Featured This Week only).
  onAdminPress?: (activity: TonyteActivity) => void;
};

const CarouselEventCard: React.FC<CardProps> = memo(({ activity, userId, width, height, showPartner, onPress, onOpenChat, onAdminPress }) => {
  const { t, language } = useI18n();
  const locale = LOCALES[language] ?? 'en-US';
  const translatedActivity = useTranslatedActivity(activity);
  const { interested, toggle, syncInterested } = useActivityInterested(getResolvedEventId(activity), userId, activity);
  const [imageSource, setImageSource] = useState<number | string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [usedImageFallback, setUsedImageFallback] = useState(false);
  const [showInterestPrompt, setShowInterestPrompt] = useState(false);
  const supportsEventChat = activity.entryType === 'official_event' && activity.sourceType !== 'club';

  useEffect(() => {
    let cancelled = false;
    setImageFailed(false);
    setUsedImageFallback(false);
    setImageSource(null);
    resolveActivityImage(activity)
      .then(resolved => {
        if (!cancelled) setImageSource(resolved.source);
      })
      .catch(() => {
        if (!cancelled) setImageFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [activity]);

  const handleInterestPress = () => {
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('auth.signInToUseFeature'));
      return;
    }
    if (interested || !supportsEventChat) {
      void toggle();
      return;
    }
    setShowInterestPrompt(true);
  };
  const handleAddToCalendar = useCallback(async () => {
    if (!userId) return;
    if (await ensureInterestedAndAddToCalendar(activity, userId, t)) syncInterested(true);
  }, [activity, syncInterested, t, userId]);
  const handleChat = useCallback(async () => {
    if (!userId) return;
    try {
      await ensureInterestedAndJoinEventChat(activity, userId, false, t);
      syncInterested(true);
      requestNotificationPermission().catch(error => {
        console.warn('[Tonyte][RN][Permissions] notification request failed', error);
      });
      onOpenChat(activity);
    } catch (error) {
      if (isEventChatExpiredError(error)) {
        showTonyteToast(t('event.chatNoLongerAvailable'), 'info', t('event.chatNoLongerAvailableBody'));
      } else {
        showTonyteToast(t('event.couldNotJoinChat'), 'error', t('common.tryAgain'));
      }
    }
  }, [activity, onOpenChat, syncInterested, t, userId]);
  const dateTimeLabel = formatCardDateTime(activity, locale);
  const placeName = activity.venueName || activity.locationName;
  const title = translatedActivity.translatingTitle ? '...' : translatedActivity.title;

  return (
    <>
    <TouchableOpacity
      activeOpacity={0.88}
      style={[styles.card, { width, height }]}
      onPress={() => onPress(activity)}
      accessibilityRole="button"
      accessibilityLabel={title}
    >
      {imageSource && !imageFailed ? (
        <Image
          source={typeof imageSource === 'number' ? imageSource : { uri: imageSource }}
          style={styles.cardImage}
          resizeMode="cover" resizeMethod="resize"
          onError={() => {
            if (!usedImageFallback) {
              setUsedImageFallback(true);
              setImageSource(getWebFallbackImage(activity));
            } else {
              setImageFailed(true);
            }
          }}
        />
      ) : (
        <LinearGradient colors={['#18283a', '#101722', '#070b14']} style={styles.cardImage} />
      )}
      <LinearGradient
        colors={['rgba(3,7,15,0.04)', 'rgba(3,7,15,0.42)', 'rgba(3,7,15,0.95)']}
        locations={[0, 0.48, 1]}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.typeBadge}>
        <Text style={styles.typeText}>
          {activity.entryType === 'community_plan' ? t('event.plan') : t(`categories.${activity.category}`, activity.category)}
        </Text>
      </View>
      <TouchableOpacity
        activeOpacity={0.82}
        style={[styles.iconButton, interested && styles.iconButtonActive]}
        onPress={event => {
          event.stopPropagation();
          handleInterestPress();
        }}
        accessibilityRole="button"
      >
        <Icon name="heart" size={12} color={interested ? Colors.turquoise : 'rgba(255,255,255,0.76)'} />
      </TouchableOpacity>
      {onAdminPress ? (
        <TouchableOpacity
          activeOpacity={0.82}
          style={styles.adminButton}
          onPress={event => {
            event.stopPropagation();
            onAdminPress(activity);
          }}
          accessibilityRole="button"
        >
          <Icon name="star" size={12} color={Colors.sunset} />
        </TouchableOpacity>
      ) : null}
      <View style={styles.copy}>
        <Text style={styles.cardTitle} numberOfLines={2}>{title}</Text>
        {placeName ? (
          <View style={styles.metaRow}>
            <Icon name="map-pin" size={9} color="rgba(255,255,255,0.62)" />
            <Text style={styles.metaText} numberOfLines={1}>{placeName}</Text>
          </View>
        ) : null}
        {dateTimeLabel ? <Text style={styles.cardTime} numberOfLines={1}>{dateTimeLabel}</Text> : null}
        {showPartner && activity.hostName ? (
          <View style={styles.partnerRow}>
            <Icon name="check-circle" size={10} color={Colors.turquoise} />
            <Text style={styles.partnerText} numberOfLines={1}>{activity.hostName}</Text>
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
    <TonyteModal
      visible={showInterestPrompt}
      title={t('event.joinEventChatQuestion')}
      message={getLumaRegistrationUrl(activity) ? `${t('event.joinEventChatPrompt')}\n\n${t('event.registerOnLumaFirst')}` : t('event.joinEventChatPrompt')}
      icon="message-circle"
      iconColor={Colors.turquoise}
      buttons={[
        { label: t('common.cancel'), variant: 'secondary', onPress: () => setShowInterestPrompt(false) },
        { label: t('event.interestedOnly'), variant: 'secondary', onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, toggle, t); } },
        { label: t('event.addToCalendar'), variant: 'teal', onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, handleAddToCalendar, t); } },
        { label: t('event.joinChat'), variant: 'primary', onPress: () => { setShowInterestPrompt(false); void registerOnLumaThen(activity, handleChat, t); } },
      ]}
      onDismiss={() => setShowInterestPrompt(false)}
    />
    </>
  );
});

// ---------------------------------------------------------------------------
// Skeleton — same footprint as the real carousel so nothing shifts when the
// picks arrive.
// ---------------------------------------------------------------------------

export const CarouselSkeleton: React.FC<{ cardWidth?: number; count?: number }> = memo(({
  cardWidth = CAROUSEL_CARD_WIDTH,
  count = 3,
}) => {
  const { theme } = useTheme();
  const themed = useMemo(() => createThemeStyles(theme), [theme]);
  const pulse = useRef(new Animated.Value(0.55)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 800, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(pulse, { toValue: 0.55, duration: 800, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [pulse]);

  return (
    <View
      style={[styles.section, Platform.OS === 'web' ? styles.fullBleedWide : styles.fullBleedMobile]}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <LinearGradient
        pointerEvents="none"
        colors={getFeaturedBackgroundColors(theme)}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.titleRow}>
        <Animated.View style={[styles.skeletonTitle, styles.skeleton, themed.skeleton, { opacity: pulse }]} />
      </View>
      <View style={styles.skeletonRow}>
        {Array.from({ length: count }, (_, index) => (
          <Animated.View
            key={index}
            style={[styles.card, styles.skeletonCard, styles.skeleton, themed.skeleton, { width: cardWidth, height: getCarouselCardHeight(cardWidth), opacity: pulse }]}
          />
        ))}
      </View>
    </View>
  );
});

// ---------------------------------------------------------------------------
// Carousel
// ---------------------------------------------------------------------------

type Props = {
  title: string;
  events: TonyteActivity[];
  status?: CarouselStatus;
  onRetry?: () => void;
  errorMessage?: string;
  retryLabel?: string;
  userId: string | null;
  cardWidth?: number;
  showPartner?: boolean;
  onCardPress: (activity: TonyteActivity) => void;
  onOpenChat: (activity: TonyteActivity) => void;
  onAdminPress?: (activity: TonyteActivity) => void;
  testID?: string;
};

const EventCarousel: React.FC<Props> = ({
  title,
  events,
  status = 'ready',
  onRetry,
  errorMessage,
  retryLabel,
  userId,
  cardWidth = CAROUSEL_CARD_WIDTH,
  showPartner,
  onCardPress,
  onOpenChat,
  onAdminPress,
  testID,
}) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemeStyles(theme), [theme]);
  const listRef = useRef<FlatList<TonyteActivity>>(null);
  const [scrollX, setScrollX] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const cardHeight = getCarouselCardHeight(cardWidth);
  const stride = cardWidth + CARD_GAP;
  const contentWidth = events.length * stride - CARD_GAP;

  const handleScroll = useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (Platform.OS === 'web') setScrollX(event.nativeEvent.contentOffset.x);
  }, []);
  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setViewportWidth(event.nativeEvent.layout.width);
  }, []);
  const scrollBy = useCallback((direction: 1 | -1) => {
    listRef.current?.scrollToOffset({
      offset: Math.max(0, scrollX + direction * stride * ARROW_STEP_CARDS),
      animated: true,
    });
  }, [scrollX, stride]);

  const renderItem = useCallback(({ item }: { item: TonyteActivity }) => (
    <CarouselEventCard
      activity={item}
      userId={userId}
      width={cardWidth}
      height={cardHeight}
      showPartner={showPartner}
      onPress={onCardPress}
      onOpenChat={onOpenChat}
      onAdminPress={onAdminPress}
    />
  ), [cardHeight, cardWidth, onAdminPress, onCardPress, onOpenChat, showPartner, userId]);

  if (status === 'loading') return <CarouselSkeleton cardWidth={cardWidth} />;

  if (status === 'error') {
    return (
      <View style={[styles.section, Platform.OS === 'web' ? styles.fullBleedWide : styles.fullBleedMobile]} testID={testID}>
        <LinearGradient
          pointerEvents="none"
          colors={getFeaturedBackgroundColors(theme)}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.titleRow}>
          <Text style={[styles.title, themed.title]}>{title}</Text>
        </View>
        <View style={[styles.errorRow, themed.errorRow]}>
          <Text style={[styles.errorText, themed.textSecondary]}>{errorMessage}</Text>
          {onRetry ? (
            <TouchableOpacity onPress={onRetry} activeOpacity={0.8} accessibilityRole="button">
              <Text style={styles.retryText}>{retryLabel}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    );
  }

  // No empty containers or orphaned headings.
  if (events.length === 0) return null;

  const isWeb = Platform.OS === 'web';
  const canScrollBack = scrollX > 4;
  const canScrollForward = viewportWidth > 0 && scrollX + viewportWidth < contentWidth - 4;

  return (
    <View style={[styles.section, Platform.OS === 'web' ? styles.fullBleedWide : styles.fullBleedMobile]} testID={testID}>
      <LinearGradient
        pointerEvents="none"
        colors={getFeaturedBackgroundColors(theme)}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <View style={styles.titleRow}>
        <View style={styles.headingCopy}>
          <Text style={[styles.title, themed.title]} accessibilityRole="header">{title}</Text>
          <Text style={[styles.subtitle, themed.subtitle]}>{t('feed.featuredSubtitle', 'The week’s standout experiences')}</Text>
        </View>
        {isWeb && (canScrollBack || canScrollForward) ? (
          <View style={styles.arrows}>
            <TouchableOpacity
              disabled={!canScrollBack}
              onPress={() => scrollBy(-1)}
              style={[styles.arrow, themed.arrow, !canScrollBack && styles.arrowDisabled]}
              accessibilityRole="button"
              accessibilityLabel="Scroll left"
            >
              <Icon name="chevron-left" size={16} color={theme.colors.icon} />
            </TouchableOpacity>
            <TouchableOpacity
              disabled={!canScrollForward}
              onPress={() => scrollBy(1)}
              style={[styles.arrow, themed.arrow, !canScrollForward && styles.arrowDisabled]}
              accessibilityRole="button"
              accessibilityLabel="Scroll right"
            >
              <Icon name="chevron-right" size={16} color={theme.colors.icon} />
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
      <FlatList
        style={styles.carouselViewport}
        ref={listRef}
        data={events}
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator={false}
        keyExtractor={item => item.id}
        renderItem={renderItem}
        contentContainerStyle={styles.list}
        ItemSeparatorComponent={Separator}
        onScroll={Platform.OS === 'web' ? handleScroll : undefined}
        onLayout={handleLayout}
        scrollEventThrottle={16}
        decelerationRate="fast"
        snapToInterval={stride}
        snapToAlignment="start"
        getItemLayout={(_, index) => ({ length: stride, offset: stride * index, index })}
        initialNumToRender={4}
        maxToRenderPerBatch={4}
        windowSize={5}
      />
    </View>
  );
};

const Separator = () => <View style={styles.separator} />;

const styles = StyleSheet.create({
  section: {
    marginBottom: 24,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: 'rgba(255,183,77,0.42)',
    backgroundColor: '#2A1717',
    paddingVertical: 18,
    paddingHorizontal: 16,
    overflow: 'hidden',
    shadowColor: '#F27D26',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 26,
    elevation: 0,
  },
  fullBleedMobile: {
    marginHorizontal: -16,
  },
  fullBleedWide: {
    marginHorizontal: -24,
  },
  titleRow: {
    minHeight: 52,
    marginBottom: 14,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  headingCopy: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    color: '#fff',
    fontSize: 20,
    lineHeight: 24,
    fontWeight: Typography.extrabold,
  },
  subtitle: {
    color: 'rgba(255,232,207,0.78)',
    marginTop: 2,
    fontSize: 11,
    lineHeight: 15,
    fontWeight: Typography.medium,
  },
  list: {
    paddingHorizontal: 16,
    paddingRight: 20,
    paddingBottom: 2,
  },
  carouselViewport: {
    marginHorizontal: -16,
  },
  separator: {
    width: CARD_GAP,
  },
  arrows: {
    flexDirection: 'row',
    gap: 8,
  },
  arrow: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    // Midnight colors — the canonical implementation (see daylightStyle.ts);
    // createThemeStyles below only ever overrides these for daylight.
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowDisabled: {
    opacity: 0.35,
  },
  card: {
    borderRadius: Radius.xl,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255,196,122,0.42)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    ...Shadow.card,
    elevation: 0,
    shadowColor: '#F2A64A',
    shadowOpacity: 0.2,
    shadowRadius: 20,
    shadowOffset: { width: 0, height: 8 },
  },
  cardImage: {
    ...StyleSheet.absoluteFill,
    width: '100%',
    height: '100%',
  },
  typeBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    maxWidth: 100,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,196,122,0.7)',
    backgroundColor: 'rgba(111,52,12,0.76)',
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  typeText: {
    color: '#FFE2BF',
    fontSize: 9,
    lineHeight: 11,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
  },
  iconButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    backgroundColor: 'rgba(10,14,22,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconButtonActive: {
    borderColor: 'rgba(38,208,206,0.4)',
    backgroundColor: 'rgba(38,208,206,0.18)',
  },
  adminButton: {
    position: 'absolute',
    top: 43,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.48)',
    backgroundColor: 'rgba(10,14,22,0.58)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  copy: {
    position: 'absolute',
    left: 12,
    right: 12,
    bottom: 14,
  },
  cardTitle: {
    color: '#fff',
    fontSize: 18,
    lineHeight: 22,
    fontWeight: Typography.extrabold,
  },
  metaRow: {
    marginTop: 5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  metaText: {
    flex: 1,
    color: 'rgba(255,255,255,0.65)',
    fontSize: 11,
    lineHeight: 14,
    fontWeight: Typography.medium,
  },
  cardTime: {
    marginTop: 5,
    color: '#FFC47A',
    fontSize: 12,
    lineHeight: 15,
    fontWeight: Typography.bold,
  },
  partnerRow: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  partnerText: {
    flex: 1,
    color: 'rgba(255,255,255,0.78)',
    fontSize: 10,
    lineHeight: 12,
    fontWeight: Typography.bold,
  },
  // Midnight colors, shared by the skeleton title pill and skeleton cards
  // below — the canonical implementation (see daylightStyle.ts);
  // createThemeStyles' `skeleton` only ever overrides this for daylight.
  skeleton: {
    backgroundColor: '#0f1520',
    borderColor: 'rgba(255,255,255,0.1)',
  },
  skeletonTitle: {
    width: 128,
    height: 15,
    borderRadius: 8,
  },
  skeletonRow: {
    flexDirection: 'row',
    gap: CARD_GAP,
    overflow: 'hidden',
    // Matches the real list's paddingBottom so the slot doesn't change height
    // when the picks replace the skeleton.
    paddingBottom: 2,
  },
  skeletonCard: {
    shadowOpacity: 0,
    elevation: 0,
  },
  errorRow: {
    minHeight: 64,
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: '#0f1520',
    borderColor: 'rgba(255,255,255,0.1)',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  errorText: {
    flex: 1,
    color: 'rgba(255,255,255,0.65)',
    fontSize: 12,
    lineHeight: 17,
    fontWeight: Typography.medium,
  },
  retryText: {
    color: Colors.sunset,
    fontSize: 12,
    fontWeight: Typography.bold,
  },
});

function getFeaturedBackgroundColors(theme: TonyteTheme): [string, string, string, string] {
  return theme.name === 'daylight'
    ? ['#FF963F', '#F27D26', '#F4B47E', theme.colors.background]
    : ['#3B2015', '#271718', '#17141A', '#0D1118'];
}

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  title: {
    color: '#FFFFFF',
    textShadowColor: theme.isDark ? 'rgba(0,0,0,0.92)' : 'rgba(43,22,12,0.92)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  subtitle: {
    color: '#FFFFFF',
    textShadowColor: theme.isDark ? 'rgba(0,0,0,0.92)' : 'rgba(43,22,12,0.92)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  skeleton: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
  }),
  arrow: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
  errorRow: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
  }),
});

export default memo(EventCarousel);
